import axiosRetry from 'axios-retry'
import TokenWrapper from './auth-token-wrapper'
import axios from 'axios'
import { API_INSTANCES, ApiInstanceKeys } from './api-instances'
import { logger } from '@sentry/react'

Object.entries(API_INSTANCES).forEach(([_key, instance]) => {
  axiosRetry(instance, {
    retryDelay: () => 1500,
    retries: 2,
    retryCondition: simpleNetworkErrorCheck,
  })
})

function simpleNetworkErrorCheck(error: any) {
  if (error.response) {
    return false
  } else if (error.request) {
    return true
  }
  return false
}

function getConfigForRequest(customConfig: any = {}) {
  const requestConfig: any = {}
  let token = TokenWrapper.getToken()

  // If no token in localStorage yet, try bootstrapping from SSO cookie
  if (
    !token &&
    !(TokenWrapper as any).isSSOIgnored?.() &&
    typeof (TokenWrapper as any).bootstrapFromSSOIfAvailable === 'function'
  ) {
    try {
      ;(TokenWrapper as any).bootstrapFromSSOIfAvailable()
      token = TokenWrapper.getToken()
    } catch (_e: any) {
      logger.error('Failed to bootstrap from SSO cookie', _e)
    }
  }

  if (customConfig.headers) {
    requestConfig.headers = { ...customConfig.headers }
  }

  if (customConfig.cancelToken) {
    requestConfig.cancelToken = customConfig.cancelToken
  }

  if (
    token &&
    (TokenWrapper.tokenIsValid() || TokenWrapper.refreshTokenIsValid())
  ) {
    requestConfig.headers = {
      ...(requestConfig.headers || {}),
      Authorization: `Bearer ${token}`,
    }
  } else {
    // Do not remove the cross-domain SSO cookie when we simply don't have
    // a local token yet (e.g., app boot before SSO handoff). Only clear
    // localStorage tokens to avoid accidental logout from SSO.
    TokenWrapper.removeLocalStorageTokensOnly()
  }

  return requestConfig
}

export const API_WRAPPER: Record<ApiInstanceKeys, any> = {
  [ApiInstanceKeys.AUTHORIZATION]: createApiMethods(
    API_INSTANCES[ApiInstanceKeys.AUTHORIZATION]
  ),
  [ApiInstanceKeys.ONBOARDING]: createApiMethods(
    API_INSTANCES[ApiInstanceKeys.ONBOARDING]
  ),
  [ApiInstanceKeys.CUSTOMER_LIFE_CYCLE]: createApiMethods(
    API_INSTANCES[ApiInstanceKeys.CUSTOMER_LIFE_CYCLE]
  ),
  [ApiInstanceKeys.PAYAUT]: createApiMethods(
    API_INSTANCES[ApiInstanceKeys.PAYAUT]
  ),
  [ApiInstanceKeys.CHARGER_MANAGEMENT]: createApiMethods(
    API_INSTANCES[ApiInstanceKeys.CHARGER_MANAGEMENT]
  ),
  [ApiInstanceKeys.DOCUMENT_MANAGEMENT]: createApiMethods(
    API_INSTANCES[ApiInstanceKeys.DOCUMENT_MANAGEMENT]
  ),
  [ApiInstanceKeys.CONTACT_FORM]: createApiMethods(
    API_INSTANCES[ApiInstanceKeys.CONTACT_FORM]
  ),
  [ApiInstanceKeys.ASSET_MANAGEMENT]: createApiMethods(
    API_INSTANCES[ApiInstanceKeys.ASSET_MANAGEMENT]
  ),
}

function createApiMethods(instance: any) {
  return {
    callDelete: (endpoint: string, params?: any, customConfig?: any) =>
      instance.delete(endpoint, {
        data: params,
        ...getConfigForRequest(customConfig),
      }),

    callGet: (endpoint: string, params?: any, customConfig?: any) =>
      instance.get(endpoint, { params, ...getConfigForRequest(customConfig) }),

    callPost: (endpoint: string, params?: any, customConfig?: any) =>
      instance.post(endpoint, params, getConfigForRequest(customConfig)),

    callPut: (endpoint: string, params?: any, customConfig?: any) =>
      instance.put(endpoint, params, getConfigForRequest(customConfig)),

    callPatch: (endpoint: string, params?: any, customConfig?: any) =>
      instance.patch(endpoint, params, getConfigForRequest(customConfig)),
  }
}

export const createCancelToken = () => {
  const CancelToken = axios.CancelToken
  const source = CancelToken.source()
  return source
}
